/**
 * Image utilities for Poshe Styles frontend.
 *
 * Resolves image paths from the database into full URLs and
 * provides a broken-image fallback handler for <img> elements.
 */

const PLACEHOLDER = 'https://via.placeholder.com/400x400?text=Poshe+Styles';
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/**
 * Resolves an image path stored in the database into a full URL.
 *
 * - Already a full URL (http/https/data:) → return as-is
 * - Starts with /uploads → prepend API base URL
 * - Relative path → prepend API base + /uploads/
 * - Falsy → return placeholder
 */
export const getImageUrl = (path, type = 'thumbnail') => {
  if (!path) return PLACEHOLDER;
  
  let finalUrl = '';
  if (path.startsWith('http') || path.startsWith('data:')) {
    finalUrl = path;
  } else if (path.startsWith('/uploads')) {
    finalUrl = `${API_BASE}${path}`;
  } else {
    finalUrl = `${API_BASE}/uploads/${path}`;
  }

  // Optimize Unsplash images
  if (finalUrl.includes('images.unsplash.com')) {
    const [baseUrl, query] = finalUrl.split('?');
    const params = new URLSearchParams(query || '');
    
    if (type === 'thumbnail') {
      params.set('w', '400');
      params.set('q', '75');
    } else if (type === 'detail') {
      params.set('w', '800');
      params.set('q', '80');
    } else if (type === 'hero') {
      params.set('w', '1200');
      params.set('q', '85');
    }
    
    params.set('auto', 'format');
    params.set('fit', 'crop');
    
    return `${baseUrl}?${params.toString()}`;
  }

  // Optimize Cloudinary images — inject transforms for auto format + quality
  if (finalUrl.includes('res.cloudinary.com') && finalUrl.includes('/upload/')) {
    // Size map based on context
    const widthMap = { thumbnail: 'w_400', detail: 'w_800', hero: 'w_1200' };
    const qualityMap = { thumbnail: 'q_auto:low', detail: 'q_auto:good', hero: 'q_auto:best' };
    const transforms = `f_auto,${qualityMap[type] || 'q_auto'},${widthMap[type] || 'w_800'}`;

    // Insert transforms after /upload/ (before the version/path segment)
    return finalUrl.replace('/upload/', `/upload/${transforms}/`);
  }

  return finalUrl;
};

/**
 * onError handler for <img> elements.
 * Swaps the broken src to a placeholder (once, to avoid infinite loops).
 *
 * Usage:  <img src={url} onError={handleImgError} />
 */
export const handleImgError = (e) => {
  if (!e.target.dataset.fallback) {
    e.target.dataset.fallback = 'true';
    e.target.src = PLACEHOLDER;
  }
};
